import type { FC } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { AddPhysicalButton } from "src/app/features/Contract/components/buttons/AddPhysicalButton";
import type { SiteContractEntity } from "src/app/features/Site/types/SiteContractEntity";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import { type ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import {
    displayDateTimeValue,
    displayDateValue,
    displayEnumValue,
    displayStatusValue,
} from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";

export interface SitesContractsTableProps extends AntCustomFormField<SiteContractEntity[]>, CommonFieldAttributes {}

export const SitesContractsTable: FC<SitesContractsTableProps> = ({ value = [], onChange, readOnly }) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback((): ExtendedColumnsType<SiteContractEntity>[] => {
        return [
            {
                title: t("contract.id"),
                key: "id",
                dataIndex: "id",
            },
            {
                title: t("contract.contractNumber"),
                key: "contractNumber",
                dataIndex: "contractNumber",
            },
            {
                title: t("genericTable.column.status"),
                key: "active",
                dataIndex: "active",
                render: (cellValue) => displayStatusValue(cellValue),
            },
            {
                title: t("contract.acquirer"),
                key: "acquirer",
                dataIndex: "acquirer",
            },
            {
                title: t("contract.appProfile"),
                key: "appProfile",
                dataIndex: "appProfile",
            },
            {
                title: t("contract.contractProfile"),
                key: "contractProfile",
                dataIndex: "contractProfile",
            },
            {
                title: t("contract.terminalType"),
                key: "terminalType",
                dataIndex: "terminalType",
            },
            {
                title: t("contract.contractCardType"),
                key: "contractCardType",
                dataIndex: "contractCardType",
                render: (cellValue) => displayEnumValue(cellValue, "contract-card-type"),
            },
            {
                title: t("contract.validFrom"),
                key: "validFrom",
                dataIndex: "validFrom",
                render: (cellValue) => displayDateValue(cellValue),
            },
            {
                title: t("contract.validTo"),
                key: "validTo",
                dataIndex: "validTo",
                render: (cellValue) => displayDateValue(cellValue),
            },
            {
                title: t("contract.changed"),
                dataIndex: "changed",
                key: "changed",
                render: (cellValue) => displayDateTimeValue(cellValue),
            },
        ];
    }, [t]);

    const navigate = useNavigate();

    const onDataChanged = (data: SiteContractEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };
    const onRowClick = (record: SiteContractEntity) => {
        const type = record.physical ? "physical" : "virtual";
        const route = `${BASE_PATH}/${ResourceName.CONTRACTS}/${type}/${FORM_ACTION.EDIT}/${
            record.id
        }?discriminator=${type.toUpperCase()}&service=true&id=${record.id}`;

        navigate(route);
    };

    const addContractButton = <AddPhysicalButton />;

    return (
        <div data-name="sites_contracts_table">
            <PropertyTable<SiteContractEntity>
                name="sites_contracts_table"
                getColumnsConfig={getColumnsConfig}
                propertyAsRowKey="id"
                tableData={value || []}
                readOnly={readOnly || false}
                onDataChanged={onDataChanged}
                customActions={[addContractButton]}
                onRow={(record: SiteContractEntity) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
            />
        </div>
    );
};
